import { inject, injectable, multiInject } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { RouterRepository } from '@core/routing/router.repository'
import type { RouteId } from '@core/routing/router.config'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { CommandSource } from '@core/interfaces/command-source'

/**
 * Open/query/highlight state for the command palette, plus the registered
 * `CommandSource`s that supply its results — the nav tree
 * (`NavigationCommandSource`) and, since the operations-and-palette plan,
 * backend search (`SearchRepository`). Explicitly Singleton-bound
 * (container.base.ts) so the state survives across the trigger
 * (TopBarComponent) and the modal (CommandPaletteComponent) being different
 * component instances — same reasoning as DialogRepository.
 */
@injectable()
export class CommandPaletteRepository implements UserScopedState {
  openPm = false
  queryPm = ''
  activeIndexPm = 0

  @multiInject(Types.ICommandSource)
  readonly sources!: readonly CommandSource[]

  private readonly routerRepository: RouterRepository

  constructor(@inject(RouterRepository) routerRepository: RouterRepository) {
    this.routerRepository = routerRepository

    makeObservable(this, {
      openPm: observable,
      queryPm: observable,
      activeIndexPm: observable,
      open: action,
      close: action,
      setQuery: action,
      setActiveIndex: action,
      resetUserScopedState: action,
    })
  }

  pathFor(routeId: RouteId): string | null {
    return this.routerRepository.findRoute(routeId)?.routeDef.path ?? null
  }

  open = () => {
    this.openPm = true
    this.queryPm = ''
    this.activeIndexPm = 0
    this.sources.forEach((source) => source.onQueryChanged(''))
  }

  close = () => {
    this.openPm = false
  }

  setQuery = (query: string) => {
    this.queryPm = query
    this.activeIndexPm = 0
    this.sources.forEach((source) => source.onQueryChanged(query))
  }

  setActiveIndex = (index: number) => {
    this.activeIndexPm = index
  }

  /**
   * Singleton-bound (container.base.ts), so without this the next user's
   * first shell render would show the previous user's palette still open
   * with their typed query. Each source's own `UserScopedState` reset (where
   * it has one — `SearchRepository` does, `NavigationCommandSource` is
   * stateless and has none) clears its own results independently.
   */
  resetUserScopedState = () => {
    this.openPm = false
    this.queryPm = ''
    this.activeIndexPm = 0
  }
}
